import React, { useEffect, useState } from 'react';
import { MapPin, Clock, Mail, Instagram, Facebook } from 'lucide-react';
import { mockData } from '../utils/mock';
import { useLanguage } from '../context/LanguageContext';
import { Reveal, Eyebrow } from './decor';
import { orderApi } from '../lib/orderApi';

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const cardStyle = { background: '#EFEADD', border: '1px solid rgba(154,117,34,0.25)', padding: '40px' };
const iconBox = {
  width: '48px',
  height: '48px',
  border: '1px solid rgba(154,117,34,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#9A7522'
};

const Location = () => {
  const { t } = useLanguage();
  const { location, contact } = mockData;
  const [hours, setHours] = useState(location.hours);

  useEffect(() => {
    orderApi.get('/hours')
      .then(({ data }) => { if (data?.hours) setHours(data.hours); })
      .catch(() => {}); // keep default mock hours if the request fails
  }, []);

  const openLink = (url) => window.open(url, '_blank', 'noopener,noreferrer');
  const isOpenDay = (day) => !hours[day].includes('Cerrado');

  const socialBtn = {
    width: '48px',
    height: '48px',
    border: '1px solid rgba(154,117,34,0.3)',
    color: '#5C5C54',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s ease, border-color 0.2s ease'
  };

  return (
    <section id="location" style={{ background: '#141414', padding: '100px 0' }} data-testid="location-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-[60px]">
        <Reveal>
          <div className="flex flex-col items-center text-center">
            <Eyebrow centered>{t('nav.ubicacion')}</Eyebrow>
            <h2 className="font-display mt-5" style={{ fontSize: 'clamp(34px, 5vw, 52px)', fontWeight: 700, color: '#F5F5F0' }}>
              {t('location.title')}
            </h2>
            <p className="font-body mt-4" style={{ fontSize: '15px', fontWeight: 300, color: '#C2C1B6', maxWidth: '560px', lineHeight: 1.8 }}>
              {t('location.subtitle')}
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 mt-14">
          {/* Card 1 - Address */}
          <Reveal>
            <div style={cardStyle} data-testid="location-address-card">
              <div style={iconBox}><MapPin className="w-5 h-5" /></div>
              <h3 className="font-display mt-5" style={{ fontSize: '22px', fontWeight: 700, color: '#161616' }}>
                {t('location.address')}
              </h3>
              <p className="font-body mt-3" style={{ fontSize: '14px', fontWeight: 300, color: '#5C5C54', lineHeight: 1.7 }}>
                {location.address}
              </p>
              <button
                onClick={() => openLink(location.links.googleMaps)}
                className="font-body uppercase mt-5 inline-flex items-center group"
                style={{ fontSize: '11px', letterSpacing: '2px', color: '#9A7522', fontWeight: 600 }}
                data-testid="location-maps-link"
              >
                {t('location.viewOnMaps')}
                <span className="ml-2 transition-transform duration-200 group-hover:translate-x-1">→</span>
              </button>
            </div>
          </Reveal>

          {/* Card 2 - Hours */}
          <Reveal delay={0.1}>
            <div style={cardStyle} data-testid="location-hours-card">
              <div style={iconBox}><Clock className="w-5 h-5" /></div>
              <h3 className="font-display mt-5" style={{ fontSize: '22px', fontWeight: 700, color: '#161616' }}>
                {t('location.hours')}
              </h3>
              <div className="mt-4 space-y-2">
                {Object.entries(hours).map(([day, dayHours]) => {
                  const open = isOpenDay(day);
                  return (
                    <div key={day} className="flex justify-between items-center">
                      <span className="font-body" style={{ fontSize: '13px', fontWeight: 300, color: open ? '#5C5C54' : 'rgba(20,20,20,0.28)' }}>
                        {t(`location.days.${day}`)}
                      </span>
                      <span className="font-body" style={{ fontSize: '13px', fontWeight: 500, color: open ? '#9A7522' : 'rgba(20,20,20,0.28)' }}>
                        {dayHours.includes('Cerrado') ? t('location.closed') : dayHours}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          {/* Card 3 - Contact */}
          <Reveal delay={0.05}>
            <div style={cardStyle} data-testid="location-contact-card">
              <div style={iconBox}><Mail className="w-5 h-5" /></div>
              <h3 className="font-display mt-5" style={{ fontSize: '22px', fontWeight: 700, color: '#161616' }}>
                {t('contactLabel')}
              </h3>
              <button
                onClick={() => openLink(`https://mail.google.com/mail/?view=cm&to=${contact.email}&su=Consulta%20desde%20la%20web`)}
                className="font-body mt-3 block text-left transition-colors duration-200"
                style={{ fontSize: '14px', fontWeight: 300, color: '#5C5C54' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#9A7522')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#5C5C54')}
                data-testid="location-email"
              >
                {contact.email}
              </button>
              <button
                onClick={() => window.open(`tel:${contact.phone}`, '_self')}
                className="font-body mt-2 block text-left transition-colors duration-200"
                style={{ fontSize: '14px', fontWeight: 300, color: '#5C5C54' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#9A7522')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#5C5C54')}
                data-testid="location-phone"
              >
                {t('location.callDirect')} · {contact.phone}
              </button>
              <button
                onClick={() => openLink(location.links.uberEats)}
                className="font-body uppercase mt-5 inline-flex items-center group"
                style={{ fontSize: '11px', letterSpacing: '2px', color: '#9A7522', fontWeight: 600 }}
                data-testid="location-ubereats"
              >
                Uber Eats
                <span className="ml-2 transition-transform duration-200 group-hover:translate-x-1">→</span>
              </button>
            </div>
          </Reveal>

          {/* Card 4 - Social */}
          <Reveal delay={0.15}>
            <div style={cardStyle} data-testid="location-social-card">
              <div style={iconBox}><Instagram className="w-5 h-5" /></div>
              <h3 className="font-display mt-5" style={{ fontSize: '22px', fontWeight: 700, color: '#161616' }}>
                {t('location.followUs')}
              </h3>
              <div className="flex gap-3 mt-5">
                {[
                  { key: 'instagram', icon: <Instagram className="w-5 h-5" />, url: location.links.instagram },
                  { key: 'facebook', icon: <Facebook className="w-5 h-5" />, url: location.links.facebook },
                  { key: 'tiktok', icon: <TikTokIcon />, url: location.links.tiktok }
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => openLink(s.url)}
                    style={socialBtn}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#9A7522'; e.currentTarget.style.borderColor = '#9A7522'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#5C5C54'; e.currentTarget.style.borderColor = 'rgba(154,117,34,0.3)'; }}
                    data-testid={`location-social-${s.key}`}
                    aria-label={s.key}
                  >
                    {s.icon}
                  </button>
                ))}
              </div>
              <p className="font-body mt-5" style={{ fontSize: '12px', fontWeight: 300, color: '#5C5C54' }}>
                {t('location.shareExperience')}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Location;
